import { MapPin } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { CityOption } from '@/types/publicSchool'

/** Radix não aceita string vazia como valor de item; o vazio só existe na URL. */
export const ALL_CITIES = 'all'

/**
 * "Perto de você": o estado em que o mapa recorta num raio em volta do visitante
 * sem ninguém ter escolhido cidade. Não é uma cidade — é o valor que representa
 * "nenhum filtro de cidade, mas a lista está em volta de mim".
 */
export const NEARBY_CITY = 'nearby'

interface CitySelectProps {
  cities: CityOption[]
  selected: string | null
  onChange: (city: string) => void
  /**
   * A cidade em que o visitante está, quando o mapa sabe onde ele está.
   *
   * Sem cidade escolhida, o controle mostra ela no lugar de "Todas as cidades":
   * dizer "todas" enquanto a lista tem 20 negócios de um raio de 120 km é o
   * controle contradizendo o resultado. Com uma cidade escolhida, ela vira a
   * opção "Perto de você" — que é o caminho de volta para o recorte.
   */
  nearbyCity?: { slug: string; label: string } | null
  className?: string
}

/**
 * Vive em dois lugares: dentro da pílula de busca no desktop e como controle
 * solto ao lado de "Filtros" no mobile. Por isso é um componente próprio, com o
 * estilo vindo por className — é controlado, então aparecer duas vezes na árvore
 * não duplica estado.
 */
export default function CitySelect({
  cities,
  selected,
  onChange,
  nearbyCity,
  className,
}: CitySelectProps) {
  // Só é o valor ATUAL sem cidade escolhida; a opção em si continua na lista
  // depois, para dar o caminho de volta.
  const emNearby = !selected && Boolean(nearbyCity)

  /**
   * O rótulo do gatilho, calculado aqui em TODOS os casos — nunca deixado a
   * cargo do Radix. Ver o comentário do `SelectValue` abaixo: alternar entre ter
   * e não ter children é o que derrubava a página.
   *
   * Sem cidade na lista de facetas (um `?city=` inventado na URL) sobra o
   * próprio slug: é feio, mas é melhor que gatilho vazio, e é o único caso em
   * que isso acontece.
   */
  const rotuloDoGatilho =
    emNearby && nearbyCity
      ? nearbyCity.label
      : selected
        ? (cities.find((city) => city.slug === selected)?.label ?? selected)
        : 'Todas as cidades'

  return (
    <Select
      value={selected ?? (emNearby ? NEARBY_CITY : ALL_CITIES)}
      onValueChange={onChange}
    >
      <SelectTrigger
        aria-label="Filtrar por cidade"
        className={cn('gap-2', className)}
      >
        <MapPin
          aria-hidden="true"
          className="h-4 w-4 shrink-0 text-muted-foreground"
        />
        {/* No gatilho vai só o nome da cidade: é o que responde "onde estou?" de
            relance. O "perto de você" fica no item aberto, onde há espaço para
            explicar que o recorte é um raio e não a cidade exata — e a contagem
            ("Fortaleza (7)") fica no item também, que é onde ela ajuda a
            escolher.

            É `SelectValue` — nunca um <span> solto. É ele o nó que o Radix mede
            para posicionar o menu (o alinhamento por item ancora o item marcado
            no texto do gatilho); trocando-o por um span, a conta perdia a
            referência e só o `top` era escrito: medido, o menu abria em y=3006
            numa tela de 900, o que deixava o filtro de cidade inalcançável no
            mapa.

            E é SEMPRE com children, nunca com `placeholder`. O Radix, quando o
            SelectValue está sem children, faz um createPortal do texto do item
            marcado PARA DENTRO deste span. Aí passam a existir dois donos do
            mesmo nó: children de um único texto viram `span.textContent = ...`
            no React, que apaga tudo lá dentro — inclusive o nó do portal. Na
            desmontagem seguinte o portal tenta remover um filho que já não está
            lá e estoura `NotFoundError: Failed to execute 'removeChild'`, que
            mata a página inteira.

            Era exatamente o que acontecia ao filtrar por cidade na vista mapa:
            só ali o servidor manda `origin_city`, então só ali o gatilho
            alternava entre ter children (perto de você) e não ter (cidade
            escolhida). Passando children sempre, o portal nunca chega a existir
            e o span tem um dono só. */}
        <SelectValue>{rotuloDoGatilho}</SelectValue>
      </SelectTrigger>
      <SelectContent align="end">
        {nearbyCity && (
          <SelectItem value={NEARBY_CITY}>
            Perto de você ({nearbyCity.label})
          </SelectItem>
        )}
        <SelectItem value={ALL_CITIES}>Todas as cidades</SelectItem>
        {cities.map((city) => (
          <SelectItem key={city.slug} value={city.slug}>
            {city.label} ({city.count})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
